import lodash from 'lodash';
import { FC, FormEvent, useMemo } from 'react';
import { EasyField } from 'react-bootstrap-formutil';
import { useNavigate } from 'react-router-dom';
import IdType from 'components/forms/IdType';
import IssueCountry from 'components/forms/IssueCountry';
import LegalResidenceCountry from 'components/forms/LegalResidenceCountry';
import { getFieldsOfPersonalInfo } from 'components/forms/PersonalInfo';
import Country from 'components/forms/PersonalInfo/components/Country';
import { getFieldsByNameType, getNameType } from 'components/forms/RealNameInfo';
import TaxCountry from 'components/forms/TaxCountry';
import UploadID, { getIdcardUploadFiles, PostOcrTypes, UploadOcrFunc } from 'components/forms/UploadID';
import StickyButton from 'components/StickyButton';
import Toast from 'components/Toast';
import { Fade } from 'components/Transition';
import { fixDataToLocal } from 'stores/security';
import values from 'stores/values';
import { CountryTypeEnum, OCRTypeEnum } from 'types/common';
import { CustomerInfoType, LicenseEnum } from 'types/customer';
import { checkFourCountryIsChn } from 'utils/helper';
import { language } from 'utils/i18n';
import withProgressForm from 'utils/withProgressForm';
import { OCRProps } from './type';

/**
 * @description 非SSN开户流程OCR识别页面
 */
const OCR: FC<OCRProps> = ({ onSubmit, $formutil }) => {
    const { $params, $setValues } = $formutil;
    const {
        temporary_ocr,
        issue_country: formIssueCountry,
        id_type: formIdType,
        country: formCountry,
        legal_residence_country: formLegalResidenceCountry,
        tax_residence_country: formTaxResidenceCountry
    } = $params;
    const { temporary_is_four_chn: oldIsFourChn, legal_residence_country: storageLegalResidenceCountry } =
        values.getParams();
    const issueCountryList = useMemo(
        () => [formCountry, formLegalResidenceCountry],
        [formCountry, formLegalResidenceCountry]
    );
    const navigate = useNavigate();
    // 如果是四个国家都是中国则保持第二期的非SSN开户流程不变 https://tigertech.feishu.cn/wiki/CPzBwLSswi6kP4kGHoFczcuFnnf
    const curIsFourChn = checkFourCountryIsChn({
        country: formCountry,
        legalResidenceCountry: formLegalResidenceCountry,
        issueCountry: formIssueCountry,
        taxResidenceCountry: formTaxResidenceCountry
    });

    // 如果证件没有识别出来用户信息(比如风景图), 对应的信息字段为空字符串(看字段类型决定)
    const handleOcrDataChange: UploadOcrFunc = (data: PostOcrTypes) => {
        if (!data) {
            Toast.show(__('证件不清晰，请重新上传'));
        } else {
            $setValues({
                temporary_ocr: data
            });
        }
    };
    const handleSubmit = (ev: FormEvent) => {
        onSubmit(() => {
            const ocrData = temporary_ocr || {};
            const nameType = getNameType({
                idType: formIdType,
                country: formCountry
            });
            // 从OCR中识别的个人信息中挑选出仅包含基础信息页面需要的字段
            const ocrBaseinfo: CustomerInfoType = lodash.pick(
                ocrData,
                getFieldsByNameType(nameType, true).concat(getFieldsOfPersonalInfo(null, false, 'baseinfo'))
            ) as CustomerInfoType;
            const ocrBaseinfo2 = lodash.pick(
                ocrData,
                getFieldsOfPersonalInfo(null, false, 'baseinfo2')
            ) as CustomerInfoType;

            // 从非四个中国变为四个中国或者从四个中国变为非四个中国，开户流程会有差异，都需要清空在不同流程中展示的页面缓存
            const hasChangedChn = (curIsFourChn && !oldIsFourChn) || (!curIsFourChn && oldIsFourChn);

            if (hasChangedChn) {
                ['baseinfo', 'baseinfo2', 'address', 'deliveryAddress', 'exemption'].forEach(key => {
                    values.mergeParams(key, {});
                });
            }

            if (storageLegalResidenceCountry !== formLegalResidenceCountry) {
                // 居住国发生变化时，需要清空地址页信息
                values.mergeParams('address', {});
            }

            const storageBaseinfo = values.getParams('baseinfo') || {};
            const storageBaseinfo2 = values.getParams('baseinfo2') || {};
            // 因为存在居住地址、有无邮寄地址、家庭婚姻等用户手动填写的信息，所以不再清空个人信息页缓存而是在原有数据基础上再填充OCR数据
            const baseinfoParams = {
                ...storageBaseinfo,
                ...fixDataToLocal(ocrBaseinfo),
                name_type: nameType
            };
            const baseinfo2Params = {
                ...storageBaseinfo2,
                ...fixDataToLocal(ocrBaseinfo2),
                // 基础信息页使用这里填写的国籍、签发国
                country: formCountry,
                born_country: formCountry,
                issue_country: formIssueCountry,
                id_type: formIdType
            };

            values.mergeParams('baseinfo', baseinfoParams);
            values.mergeParams('baseinfo2', baseinfo2Params);

            navigate('../baseinfo');
        }, ev);
    };

    return (
        <form className="open-ocr-root" noValidate onSubmit={handleSubmit}>
            <h3 className="open-title mb-2">{__('PLEASE UPLOAD YOUR ID')}</h3>
            <p className="fs-6 fw-normal normal-text-color mb-4 lh-1">
                {__('We need some of your personal information before we can create an account')}
            </p>
            <Country
                isSSN={false}
                countrySelectProps={{
                    needLocation: true,
                    type: CountryTypeEnum.NON_SSN_CITIZENSHIP
                }}
            />
            <LegalResidenceCountry
                isSSN={false}
                countrySelectProps={{
                    needLocation: true
                }}
                $onFieldChange={(value: string) => {
                    $setValues({
                        // 纳税国保持和居住国一致
                        tax_residence_country: value
                    });
                }}
            />
            <IssueCountry
                isSSN={false}
                countrySelectProps={{
                    limitCountryList: issueCountryList,
                    type: CountryTypeEnum.ALL_COUNTRY
                }}
            />
            {/* 纳税国默认和居住国一致 */}
            <TaxCountry type="hidden" $defaultValue={formLegalResidenceCountry} isSSN={false} />
            <IdType issueCountry={formIssueCountry} />
            <Fade in={!!formIdType} exit={false}>
                <UploadID
                    uploadFiles={getIdcardUploadFiles(formIdType)}
                    ocrOptions={{
                        onOCRDataChange: handleOcrDataChange,
                        license: LicenseEnum.MARSCO,
                        ocrType: OCRTypeEnum.ID_CARD
                    }}
                    idCardInfoOptions={{
                        idType: formIdType,
                        issueCountry: formIssueCountry
                    }}
                />
            </Fade>
            <p className="normal-text-color">
                {__(
                    'If you are not a resident of any of the listed countries, please apply for an account on <Tiger Trade>. '
                )}
                <a href={`https://www.itiger.com/dlapp?lang=${language}`} target="_blank" data-inapp-system>
                    {__('By clicking this link')}
                </a>
                {__(
                    ', you will exit the TradeUP app and you will be brought to an affiliate’s app offered through Tiger Brokers.'
                )}
            </p>
            {/* 开户流程可以直接使用这个变量判断是否是四个中国 */}
            {curIsFourChn && <EasyField type="hidden" name="temporary_is_four_chn" $defaultValue={curIsFourChn} />}
            <EasyField type="hidden" name="temporary_ocr" />
            <StickyButton block type="primary" size="lg" htmlType="submit" btnContent={__('Continue')} />
        </form>
    );
};

export default withProgressForm({
    id: 'ocr',
    basePath: 'open',
    ensure: 'account',
    showProgressBar: false,
    eventParams: {
        exposureEventParams: {
            model: '开户-OCR证件上传',
            name: 'OCR证件上传-页面曝光'
        },
        submitEventParams: {
            model: '开户-OCR证件上传',
            name: 'OCR证件上传-点击下一步'
        }
    }
})(OCR);
export * from './type';
